import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { achievementSection } from "../../portfolio";

export default function AchievementModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="achievements">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-amber-900/20 dark:to-yellow-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute right-0 top-1/3 w-96 h-96 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, -50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-sm font-semibold shadow-lg">
              {achievementSection.title}
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            Achievements & Certifications
          </h2>
          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            {achievementSection.subtitle}
          </p>
        </motion.div>

        {/* Achievements Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto"
        >
          {achievementSection.achievementsCards.map((achievement, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{
                y: -10,
                transition: { duration: 0.3 }
              }}
              className="glass backdrop-blur-xl rounded-2xl overflow-hidden bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-300"
            >
              {/* Image Container */}
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-amber-500/10 to-yellow-500/10">
                <motion.img
                  src={achievement.image}
                  alt={achievement.title}
                  className="w-full h-full object-contain p-6"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.4 }}
                />

                {/* Trophy Icon Overlay */}
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center text-white shadow-lg">
                    🏆
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className={`text-xl font-bold mb-3 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}>
                  {achievement.title}
                </h3>

                <p className={`text-sm mb-6 leading-relaxed line-clamp-3 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}>
                  {achievement.subtitle}
                </p>

                {/* Footer Links */}
                {achievement.footerLink && achievement.footerLink.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {achievement.footerLink.map((link, linkIndex) => (
                      <motion.a
                        key={linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 flex items-center gap-1 ${
                          isDark
                            ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        }`}
                      >
                        <span>{link.name}</span>
                        <svg className="w-3 h-3" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                          <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </motion.a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
